import React, { useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

// The name on one creative or one stack. Commits on blur and on Enter — the
// only inline-edit idiom this tool uses (GenerateDialog's selling-point edit)
// — with no debounce, because there is no debounce helper in this repo and a
// name is not worth inventing one for.
//
// A name is NOT shared between two panels the way a group's ad copy is, so
// none of AdCopyPanel's seeded/dirty reconciliation applies. What remains is
// ordinary staleness: the parent reloads after every save and re-derives the
// row, and that reload must not read back as somebody else's edit. Hence the
// snapshot — set BEFORE the caller reloads — of the value we ourselves saved.

interface Props {
  /** The stored name, or null when the creative has never been named. */
  value: string | null;
  /** Persist. Resolves once saved; the caller reloads after. */
  onSave: (name: string) => Promise<void>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export default function CreativeNameField({
  value, onSave, label = 'Name', placeholder = 'Name this creative…', disabled = false,
}: Props) {
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The last value this field itself put on the server. A reload echoing it
  // back is our own write, not an edit to adopt.
  const ownWrite = useRef<string | null>(null);
  // What the server is known to hold — the baseline a blur is compared against.
  // NOT the `value` prop, which lags behind our own saves until the parent's
  // reload lands: comparing against it swallows a revert typed in that window.
  const committed = useRef(value || '');

  useEffect(() => {
    const incoming = value || '';
    if (ownWrite.current !== null && incoming === ownWrite.current) return;
    ownWrite.current = null;
    committed.current = incoming;
    setDraft(incoming);
  }, [value]);

  const commit = async () => {
    const next = draft.trim();
    if (next === committed.current.trim()) return;
    ownWrite.current = next;
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      committed.current = next;
    } catch (err: any) {
      ownWrite.current = null;
      setError(err?.response?.data?.error || err?.message || 'Saving the name failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      placeholder={placeholder}
      value={draft}
      disabled={disabled || saving}
      error={!!error}
      helperText={error || undefined}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      InputProps={{
        endAdornment: saving ? (
          <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
        ) : undefined,
      }}
    />
  );
}
